import axios from "axios";
import { decodeToken } from "./commonFunctions";
// import { SchoolSignupContext } from "../context/SchoolSignupContext";
// import { useChatContext } from "../../context/ChatContext";

const getAuthToken = () => localStorage.getItem("access_token");

const API_URL = process.env.REACT_APP_BASE_URL;
const API_URL_BASE = process.env.REACT_APP_BASE_URL_BASE;
const API_URL_BASE_AI = process.env.REACT_APP_BASE_URL_AI;

const FINAL_PLAN_MARKERS = [
  "BC CURRICULUM UNIT PLAN",
  "BC CURRICULUM LESSON PLAN",
  "INDIVIDUAL EDUCATION PLAN",
  "INDIVIDUALIZED EDUCATION PLAN",
  "CB IEP",
  "IEP PLAN",
];

const normalizePlanHeaderLine = (line) =>
  line
    .trim()
    .replace(/^\*+|\*+$/g, "")
    .replace(/^#+\s*/, "")
    .replace(/<[^>]+>/g, "")
    .trim()
    .toUpperCase();

const isPlanHeaderLine = (line, marker) => {
  const trimmed = normalizePlanHeaderLine(line);
  if (!trimmed) return false;
  return (
    trimmed === marker ||
    trimmed.startsWith(`${marker} `) ||
    trimmed.startsWith(`${marker}:`) ||
    trimmed.startsWith(`${marker}-`) ||
    trimmed.startsWith(`${marker}—`)
  );
};

/** True when the message contains a plan document title line, not a casual mention in prose. */
export const isFinalPlanMessage = (text) => {
  if (!text || typeof text !== "string") return false;
  const lines = text.toUpperCase().split("\n");
  return FINAL_PLAN_MARKERS.some((marker) =>
    lines.some((line) => isPlanHeaderLine(line, marker))
  );
};

/** Remove decorative separator lines made only of box-drawing double horizontal characters (═). */
export const stripDocSeparatorLines = (text) => {
  if (!text || typeof text !== "string") return text;
  return text
    .split("\n")
    .filter((line) => !/^\s*[═]+\s*$/.test(line))
    .join("\n");
};

const planDocStorageKey = (conversationId) => `plan_doc_${conversationId}`;

export const savePlanDocToSession = (conversationId, meta) => {
  if (!conversationId || !meta) return;
  try {
    sessionStorage.setItem(planDocStorageKey(conversationId), JSON.stringify(meta));
  } catch (e) {
    console.warn("Could not cache plan doc:", e);
  }
};

export const loadPlanDocFromSession = (conversationId) => {
  if (!conversationId) return null;
  try {
    const raw = sessionStorage.getItem(planDocStorageKey(conversationId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const downloadPlanDocxFromBase64 = (documentBase64, filename = "plan.docx") => {
  if (!documentBase64) return;
  const byteChars = atob(documentBase64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "plan.docx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const fetchPlanDocxFromText = async (finalOutput, planType = "unit") => {
  const cleanedOutput = stripDocSeparatorLines(finalOutput);
  const response = await fetch(`${API_URL_BASE_AI}/generate_plan_docx`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ final_output: cleanedOutput, plan_type: planType }),
  });
  if (!response.ok) {
    throw new Error("Failed to generate document");
  }
  return response.json();
};

export const getGoogleDriveStatus = async (userId) => {
  const response = await fetch(
    `${API_URL_BASE_AI}/google_drive/status?user_id=${encodeURIComponent(userId)}`
  );
  if (!response.ok) throw new Error("Could not check Google Drive status");
  return response.json();
};

export const saveGoogleDriveFolder = async (userId, folderUrl) => {
  const response = await fetch(`${API_URL_BASE_AI}/google_drive/folder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, folder_url: folderUrl }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || "Could not save Google Drive folder");
  }
  return data;
};

export const completeGoogleDriveAuth = async (code, state) => {
  const response = await fetch(`${API_URL_BASE_AI}/google_drive/complete_auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || "Google Drive connection failed");
  }
  return data;
};

export const getGoogleDriveAuthUrl = async (userId, frontendRedirect, oauthRedirectUri) => {
  const params = new URLSearchParams({
    user_id: String(userId),
    frontend_redirect: frontendRedirect,
  });
  if (oauthRedirectUri) {
    params.set("oauth_redirect_uri", oauthRedirectUri);
  }
  const response = await fetch(`${API_URL_BASE_AI}/google_drive/auth_url?${params}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || "Google Drive auth unavailable");
  }
  return response.json();
};

export const uploadPlanToGoogleDrive = async ({
  userId,
  documentBase64,
  documentFilename,
  finalOutput,
  planType,
}) => {
  const response = await fetch(`${API_URL_BASE_AI}/google_drive/upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      document_base64: documentBase64 || null,
      document_filename: documentFilename || "plan.docx",
      final_output: finalOutput || null,
      plan_type: planType || "unit",
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.detail?.message || data.detail || "Google Drive upload failed");
    err.needAuth = data.detail?.need_auth === true;
    throw err;
  }
  return data;
};

// gpt-4o returns message.content as array [{ type: "text", text: "..." }]; backend may use .data.data or .data
export function getMessageContentFromResponse(response) {
  const choices = response?.data?.data?.choices ?? response?.data?.choices;
  const message = choices?.[0]?.message;
  if (!message) return null;
  let content = message.content;
  if (content == null) return null;
  if (Array.isArray(content)) {
    const textPart = content.find((p) => p.type === "text" && p.text);
    return textPart ? textPart.text : content.map((p) => p.text || "").join("").trim() || null;
  }
  return typeof content === "string" ? content : null;
}

// const { signupData, setSignupData,signupDataSession, setSignupDataSession } = React.useContext(SchoolSignupContext);

// const API_URL = "http://localhost:8000/api/v1"; // or your backend URL

//  const { titleId ,setTitleId,setSidelistUpdate} = useChatContext();


// export const InitializeConversationHistory = async (title, question, user_id) => {
//   try {
//     const response = await axios.post(`${API_URL}/history/conversations`, {
//       title,
//       question,
//       user_id,
//     });

//     return response.data;
//   } catch (error) {
//     console.error("❌ Error saving conversation:", error.response ? error.response.data : error.message);
//     throw error;
//   }
// };

export const InitializeConversationHistory = async (title, question, user_id, schoolType) => {
  const token = getAuthToken();
  try {
    const response = await axios.post(
      `${API_URL}/history/conversations`,
      {
        title,
        question,
        user_id,
        schoolType
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Error saving conversation:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

export const SaveConversationHistory = async (messages) => {
  const token = getAuthToken();
  try {
    const response = await axios.post(`${API_URL}/history/messages`, messages , {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    return response.data;
  } catch (error) {
    console.error("❌ Error saving messages:", error.response ? error.response.data : error.message);
    throw error;
  }
};

export const GetConversationHistory = async (titleId, setAllMessage, historySize) => {
  try {
    const response = await axios.get(`${API_URL}/history/messages/${titleId}`);
    const data = response.data?.data ?? [];
    const list = Array.isArray(data) ? data : [];
    if (historySize) {
      setAllMessage(list.slice(0, historySize));
    } else {
      setAllMessage(list);
    }
  } catch (error) {
    console.error('Error fetching conversation:', error);
    setAllMessage([]);
  }
};


export const SidebarTitle = async (userId, setConversationHistory, searchValue) => {
  if (userId == null || userId === undefined) return;
  const token = getAuthToken();
  try {
    const response = await axios.get(`${API_URL}/history/conversations/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    params: {
      title: searchValue || "",
    },
  });
    setConversationHistory(response.data.data);
  } catch (error) {
    console.error('Error fetching conversation:', error);
  }
};


export const DeleteConversations = async (id, setSidelistUpdate) => {
  try {
    await axios.delete(`${API_URL}/history/conversations/${id}`);
    setSidelistUpdate(id + 1)
  } catch (error) {
    console.error('Error fetching conversation:', error);
  }
};


export const TitleUpdate = async (conversation_id, user_id, title) => {
  try {
    const response = await axios.put(
      `${API_URL}/history/conversations`,
      {
        conversation_id,
        user_id,
        title
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error updating conversation title:", error);
    throw error;
  }
};

export const MessageUpdate = async (message_id, conversation_id, sender, message, setSidelistUpdate) => {
  try {
    const response = await axios.put(
      `${API_URL}/history/messages`,
      {
        message_id,
        conversation_id,
        sender,
        message
      }
    );
    setSidelistUpdate(response)
    return response.data;
  } catch (error) {
    console.error("❌ Error updating conversation title:", error);
    throw error;
  }
};


export const getLessonPlanResponse = async (userInput, chatHistory, predefinedAnswers) => {
  try {
    const res = await axios.post(`${API_URL}/LessonPlaning/chat-Lesson-plan`, {
      user_input: userInput,
      chat_history: chatHistory,
      predefined_awnser: predefinedAnswers
    });
    return res.data;
  } catch (err) {
    console.error("Lesson Plan API Error:", err);
    alert("Error calling Lesson Plan API");
    throw err;
  }
};

export const getAssignmentPlanResponse = async (userInput, chatHistory, predefinedAnswers) => {
  // var a = JSON.stringify(predefinedAnswers, null, 2)
  try {
    const res = await axios.post(`${API_URL}/AssignmentPlaning/chat-Assignment-plan`, {
      user_input: userInput,
      chat_history: chatHistory,
      predefined_answer: predefinedAnswers
    });
    return res.data;
  } catch (err) {
    console.error("Assignment Plan API Error:", err);
    alert("Error calling Assignment Plan API");
    throw err;
  }
};

export const getAssessmentPlanResponse = async (userInput, chatHistory, predefinedAnswers) => {
  try {
    const res = await axios.post(`${API_URL}/AssessmentPlaning/chat-Assessment-plan`, {
      user_input: userInput,
      chat_history: chatHistory,
      predefined_awnser: predefinedAnswers
    });
    return res.data;
  } catch (err) {
    console.error("Assessment Plan API Error:", err);
    alert("Error calling Assessment Plan API");
    throw err;
  }
};

export const getUnitPlanResponse = async (userInput, chatHistory, predefinedAnswers) => {
  try {
    const res = await axios.post(`${API_URL}/UnitPlaning/chat-unit-plan`, {
      user_input: userInput,
      chat_history: chatHistory,
      predefined_awnser: predefinedAnswers
    });
    return res.data;
  } catch (err) {
    console.error("Unit Plan API Error:", err);
    alert("Error calling Unit Plan API");
    throw err;
  }
};

// src/utils/generateTitle.js
// import axios from "axios";

// export const generateTitle = async (inputText, predefinedAnswers, existingTitles = []) => {
//   console.log(existingTitles,"existingTitles")
//   try {
//     const last20Titles = Array.isArray(existingTitles) ? existingTitles.slice(0, 20) : [];
//     const existingTitlesStr = last20Titles?.length
//       ? `\nThese titles are already used (generate a different, unique title - do not repeat any): ${last20Titles.join(", ")}`
//       : "";

//     const response = await axios.post(
//       `${API_URL_BASE}/api/chatgpt/openAI/completions`,
//       {
//         model: "gpt-3.5-turbo",

//         messages: [
//           {
//             role: "system",
//             content:
//           `You are a helpful assistant that generates concise, spiritually aligned, and relevant sidebar titles for educational content related to Christian education, lesson planning, and learning supports. Strictly follow these rules when creating the title:
//           - The title must be exactly 2 or 3 plain words.
//           - Do not use any special characters, punctuation, or symbols.
//           - Only use clear, simple, meaningful words.
//           - The title should be relevant to the topic and overall content.
//           - Keep the title spiritually appropriate and aligned with Christian values.
//           - The title must be unique and must NOT be the same as any existing title you are given.`
//           },
//           {
//             role: "user",
//             content: `Topic: ${predefinedAnswers || 'N/A'}

//           Full Content:
//           ${inputText}
//           ${existingTitlesStr}

//           Generate a unique sidebar title that strictly follows the above rules.`
//           }
//         ],

//         max_tokens: 10,
//         temperature: 0.7,
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//         },
//       }
//     );


//     return getMessageContentFromResponse(response);
//   } catch (error) {
//     console.error("Error generating title:", error);
//     return null;
//   }
// };

export const generateTitle = async (inputText, predefinedAnswers, existingTitles = [], options = {}) => {
  const fromLearningSupports = options?.fromLearningSupports === true;

  try {
    const last20Titles = Array.isArray(existingTitles) ? existingTitles.slice(0, 20) : [];
    const existingTitlesStr = last20Titles?.length
      ? `\nAvoid repeating these existing titles: ${last20Titles.join(", ")}`
      : "";

    const systemContent = fromLearningSupports
      ? `You are a professional editor for a Learning Supports / IEP platform.
Create a sidebar title for this conversation.
Rules: The title MUST contain the student's name (extract from content; if not found use "Student"). Use Title Case, 2–6 words. No extra punctuation except a hyphen. Output only the title.`
      : `You are a professional editor for a Christian Education platform.
Create a descriptive, high-quality sidebar title based on the provided content.
Strict Rules:
- Length: Between 2 and 5 words maximum.
- Format: Use Title Case (e.g., 'Kingdom of Heaven Study').
- Purpose: The title must summarize the specific lesson or theological topic.
- Restrictions: No punctuation, no symbols, and no generic placeholders.
- Uniqueness: Ensure the title feels distinct from common labels.
- Tone: Dignified, educational, and spiritually centered.`;

    const userContent = fromLearningSupports
      ? `Topic: ${predefinedAnswers || 'Learning Supports'}
Content: ${inputText.substring(0, 700)}
${existingTitlesStr}
Generate a sidebar title that must contain the student's name. Output only the title.`
      : `Topic: ${predefinedAnswers || 'General Lesson'}
Content: ${inputText.substring(0, 700)}
${existingTitlesStr}
Generate a unique, descriptive title (2-5 words) now:`;

    const response = await axios.post(
      `${API_URL_BASE}/api/chatgpt/openAI/completions`,
      {
        model: "gpt-3.5-turbo",
        messages: [
          { role: "system", content: systemContent },
          { role: "user", content: userContent }
        ],
        max_tokens: 15,
        temperature: 0.6,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    return getMessageContentFromResponse(response);
  } catch (error) {
    console.error("Error generating title:", error);
    return fromLearningSupports ? "New Learning Supports" : "New Christian Education Lesson";
  }
};
 

export const christianEducation = async (userInput, chatHistory, predefinedAnswers, type,attachedFiles,allAttachments=[], onStreamChunk) => {


//  console.log(userInput, "userInput 999")
//   console.log(attachedFiles, "attachedFiles 999")
//   console.time("christianEducation Execution Time");

const school_type = localStorage.getItem("school_type");
const userMasterTypeId = Number(decodeToken()?.masterTypeid);
console.log(school_type,"school_type")
console.log(allAttachments,"allAttachments 999")

  // TFT / SCSBC: fetch profile fields and send in payload
  let deephope = null;
  let storyline = null;
  let profileClass = null;
  let profileSubject = null;
  let profileAddress = null;
  let profileSchoolName = null;
  let profilePlaceName = null;
  let profilePincode = null;
  let profileLatitude = null;
  let profileLongitude = null;
  const isTft = school_type === "tft" || type === "tft" || userMasterTypeId === 1;
  const isScsbc =
    school_type === "scsbc" ||
    type === "scsbc" ||
    Number(type) === 2 ||
    userMasterTypeId === 2 ||
    API_URL_BASE_AI?.includes("scsbc");
  if ((isTft || isScsbc) && API_URL) {
    try {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        const profileRes = await axios.get(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (profileRes.data?.code === 200 && profileRes.data?.data) {
          const profileData = profileRes.data.data;
          deephope = profileData.deephope ?? null;
          storyline = profileData.storyline ?? null;
          profileClass = profileData.class ?? profileData.profile_class ?? null;
          profileSubject = profileData.subject ?? profileData.profile_subject ?? null;
          profileAddress = profileData.address ?? null;
          profileSchoolName = profileData.schoolName ?? profileData.school_name ?? null;
          profilePlaceName = profileData.placeName ?? profileData.place_name ?? null;
          profilePincode = profileData.pincode ?? null;
          profileLatitude = profileData.latitude ?? null;
          profileLongitude = profileData.longitude ?? null;
        }
      }
    } catch (e) {
      console.warn("Profile fetch failed:", e?.message);
    }
  }

  try {
    const cleanedChatHistory = chatHistory.map(entry => ({
      message: entry.message,
      sender: entry.sender
    }));

    const payload = {
      user_input: userInput,
      chat_history: cleanedChatHistory,
      predefined_answer: predefinedAnswers,
      type: school_type === "tft" ? "tft" : type,
      attachedFiles: attachedFiles?[...attachedFiles,...allAttachments]:[]
    };
    if (isTft) {
      if (deephope != null) payload.deephope = deephope;
      if (storyline != null) payload.storyline = storyline;
    }
    if (isScsbc) {
      if (profileClass != null) payload.profile_class = profileClass;
      if (profileSubject != null) payload.profile_subject = profileSubject;
    }
    if (profileAddress != null) payload.address = profileAddress;
    if (profileSchoolName != null) payload.schoolName = profileSchoolName;
    if (profilePlaceName != null) payload.placeName = profilePlaceName;
    if (profilePincode != null) payload.pincode = profilePincode;
    if (profileLatitude != null) payload.latitude = profileLatitude;
    if (profileLongitude != null) payload.longitude = profileLongitude;

    // const response = await fetch(`https://scsbcbackai.makemagento.com/christian_education`, {
    const response = await fetch(`${API_URL_BASE_AI}/christian_education`, {

      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Streaming request failed: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";
    let fullResponse = "";
    let finalOutput = "";
    let christianEducationSection = "";
    let isFinalPlan = false;
    let planType = type;
    let documentBase64 = null;
    let documentFilename = null;
    let documentLink = null;
    let googleDocsLink = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data: ")) continue;

        const data = trimmed.slice(6);
        try {
          const json = JSON.parse(data);

          if (json.done) {
            finalOutput = json.final_output || json.final_response || fullResponse;
            christianEducationSection = json.christian_education || "";
            isFinalPlan = Boolean(json.is_final_plan);
            planType = json.plan_type || type;
            documentBase64 = json.document_base64 || null;
            documentFilename = json.document_filename || null;
            documentLink = json.document_link || documentFilename;
            googleDocsLink = json.google_docs_link || null;
            fullResponse = finalOutput || fullResponse;
            continue;
          }

          const chunk = json.chunk;
          if (json.full_response) {
            fullResponse = json.full_response;
          }

          if (chunk && onStreamChunk) {
            onStreamChunk(chunk);
          }
        } catch (err) {
          console.error("❌ Failed to parse stream JSON:", err, data);
        }
      }
    }

    console.timeEnd("christianEducation Execution Time");

    return {
      response: fullResponse,
      finalOutput: finalOutput || fullResponse,
      christianEducation: christianEducationSection,
      isFinalPlan,
      planType,
      documentBase64,
      documentFilename,
      documentLink,
      googleDocsLink,
    };

  } catch (err) {
    console.timeEnd("christianEducation Execution erro Time");
    console.error("Unit Plan API Error:", err);
    throw err;
  }
};




export const copyData = async (conversation_id, user_id) => {
  const uid = user_id ?? decodeToken()?.user_id;
  if (conversation_id == null || uid == null) {
    console.warn("copyData: conversation_id or user_id missing", { conversation_id, user_id: uid });
    throw new Error("copyData requires conversation_id and user_id");
  }
  const authToken = localStorage.getItem("access_token");
  try {
    const response = await axios.put(
      `${API_URL}/history/copyData`,
      {
        conversation_id: Number(conversation_id),
        user_id: uid
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json"
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error calling copyData API:", error);
    throw error;
  }
};



